import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import pelucheImage from './../../../assets/images/product-teddy.jpg';
import rideImage from './../../../assets/images/product-ride.jpg';
import productTshirt from './../../../assets/images/product-tshirt.jpg';
import productSinpe from './../../../assets/images/product-sinpe.jpg';

export interface ParticipationOption {
  id: string;
  title: string;
  subtitle: string;
  image: string;
  imageAlt: string;
  bulletPoints: string[];
  buttonText: string;
  actionType: 'waze' | 'register' | 'buy' | 'donate' | 'custom';
  actionUrl?: string;
}

@customElement('rftw-market')
export class RftwMarket extends LitElement {
  /** Section small header tag */
  @property({ type: String }) sectionTag = 'CÓMO AYUDAR';

  /** Main section title */
  @property({ type: String }) title = 'Maneras de participar';

  /** Section description / instruction line */
  @property({ type: String }) description =
    'Elige la que más se te ajuste, todas apoyan directamente a IAR Nosara.';

  /** Participation options for the 2x2 grid */
  @property({ type: Array }) options: ParticipationOption[] = [
    {
      id: 'peluches',
      title: 'Recolección de peluches',
      subtitle:
        'Para las crías huérfanas un peluche funciona como "madre sustituta"',
      image: pelucheImage,
      imageAlt: 'Cría de mono recostada sobre un peluche',
      bulletPoints: [
        'Recolección abierta hasta el 15 de noviembre',
        'Puntos de acopio Elephant Moto, Santa Ana.',
        'Peluches en buen estado',
      ],
      buttonText: 'Waze',
      actionType: 'waze',
      actionUrl:
        'https://ul.waze.com/ul?venue_id=180748387.1807549409.14215407&navigate=yes&zoom=17',
    },
    {
      id: 'inscripcion',
      title: 'Inscripción al ride',
      subtitle:
        'Súmate a la rodada Edition 01 por los trillos de Guanacaste. Tu inscripción financia directamente el equipo médico del refugio.',
      image: rideImage,
      imageAlt: 'Motociclistas en ruta off-road',
      bulletPoints: [
        'Precio: $50',
        'Fecha: 14 - 15 de noviembre',
        'Incluye: ruta GPX, jersey oficial y visita guiada al refugio IAR Nosara',
      ],
      buttonText: 'Inscribirme ahora',
      actionType: 'register',
    },
    {
      id: 'camisetas',
      title: 'Compra tu camiseta',
      subtitle:
        '¿No vas a rodar pero quieres apoyar la causa? El 100% de las ganancias de cada camiseta financia el equipo médico de IAR Nosara.',
      image: productTshirt,
      imageAlt: 'Rider vistiendo el jersey oficial de Ride for the Wild',
      bulletPoints: ['Jersey: $40', 'Camiseta casual: $30'],
      buttonText: 'Comprar',
      actionType: 'buy',
    },
    {
      id: 'donacion',
      title: 'Donación',
      subtitle:
        'Podés hacer una donación voluntaria y apoyar directamente a IAR Nosara. El 100% de tu aporte se destina a la causa.',
      image: productSinpe,
      imageAlt: 'Rider apoyando la causa',
      bulletPoints: [
        'Elige entre $5, $25 o $100',
        'Completa el pago en la plataforma segura',
      ],
      buttonText: 'Donar ahora',
      actionType: 'donate',
    },
  ];

  static styles = css`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    .market-container {
      background-color: #f8f6f0;
      padding: 4.5rem 2rem;
      box-sizing: border-box;
    }

    .content-wrapper {
      max-width: 1200px;
      margin: 0 auto;
    }

    .section-tag {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--wa-color-orange-50, #d95328);
      margin-bottom: 0.5rem;
    }

    .section-title {
      font-size: clamp(2rem, 4vw, 2.75rem);
      font-weight: 800;
      color: #1b2e23;
      margin: 0 0 0.5rem 0;
      letter-spacing: -0.02em;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .description {
      font-size: 0.98rem;
      line-height: 1.6;
      color: var(--wa-color-text-normal, #4a5568);
      margin: 0 0 3rem 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    /* 2x2 Grid Layout */
    .grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.75rem;
    }

    /* Card Component */
    .card {
      background-color: #ffffff;
      border-radius: 12px;
      display: flex;
      overflow: hidden;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
    }

    .card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    }

    .card-image-col {
      width: 42%;
      flex-shrink: 0;
      min-height: 250px;
    }

    .card-image {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    .card-content-col {
      width: 58%;
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      box-sizing: border-box;
    }

    .card-title {
      font-size: 1.2rem;
      font-weight: 700;
      color: #1b2e23;
      margin: 0 0 0.5rem 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .card-subtitle {
      font-size: 0.85rem;
      line-height: 1.45;
      color: var(--wa-color-text-normal, #4a5568);
      margin: 0 0 1rem 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .bullet-list {
      list-style-type: disc;
      padding-left: 1.1rem;
      margin: 0 0 1.25rem 0;
    }

    .bullet-list li {
      font-size: 0.825rem;
      line-height: 1.5;
      color: var(--wa-color-text-normal, #4a5568);
      margin-bottom: 0.25rem;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .bullet-list li strong {
      color: #1b2e23;
    }

    /* Action Buttons */
    .btn-action {
      width: 100%;
      background-color: var(--wa-color-orange-50, #ff4500);
      color: #ffffff;
      border: none;
      padding: 0.75rem 1rem;
      font-size: 0.9rem;
      font-weight: 700;
      border-radius: 6px;
      cursor: pointer;
      text-align: center;
      text-decoration: none;
      box-sizing: border-box;
      transition: background-color 0.2s ease;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .btn-action:hover,
    .btn-action:focus-visible {
      background-color: #e03d00;
      outline: none;
    }

    /* Responsive Adjustments */
    @media (max-width: 1024px) {
      .grid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 600px) {
      .market-container {
        padding: 3rem 1.25rem;
      }

      .card {
        flex-direction: column;
      }

      .card-image-col {
        width: 100%;
        min-height: 0;
        aspect-ratio: 16 / 9;
      }

      .card-content-col {
        width: 100%;
        padding: 1.25rem;
      }

      .btn-action {
        min-height: 3rem;
      }
    }
  `;

  private _handleActionClick(option: ParticipationOption) {
    if (option.actionUrl) {
      window.open(option.actionUrl, '_blank', 'noopener,noreferrer');
    }

    /** Dispatches custom event for parent component / router handling */
    this.dispatchEvent(
      new CustomEvent('market-action', {
        bubbles: true,
        composed: true,
        detail: {
          optionId: option.id,
          actionType: option.actionType,
        },
      })
    );
  }

  render() {
    return html`
      <section class="market-container" aria-label="Maneras de participar">
        <div class="content-wrapper">
          ${this.sectionTag
            ? html`<div class="section-tag">${this.sectionTag}</div>`
            : ''}

          <h2 class="section-title">${this.title}</h2>

          <p class="description">${this.description}</p>

          <div class="grid">
            ${this.options.map(
              (opt) => html`
                <div class="card" id=${opt.id}>
                  <div class="card-image-col">
                    <img
                      class="card-image"
                      src=${opt.image}
                      alt=${opt.imageAlt}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <div class="card-content-col">
                    <div>
                      <h3 class="card-title">${opt.title}</h3>
                      <p class="card-subtitle">${opt.subtitle}</p>

                      ${opt.bulletPoints && opt.bulletPoints.length > 0
                        ? html`
                            <ul class="bullet-list">
                              ${opt.bulletPoints.map(
                                (bp) => html`<li>${bp}</li>`
                              )}
                            </ul>
                          `
                        : ''}
                    </div>

                    <button
                      class="btn-action"
                      @click=${() => this._handleActionClick(opt)}
                    >
                      ${opt.buttonText}
                    </button>
                  </div>
                </div>
              `
            )}
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-market': RftwMarket;
  }
}

